import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Video, Phone, Calendar, Clock, Users, Settings,
  Play, Pause, PhoneOff, Monitor, MessageSquare,
  Mic, MicOff, Camera, CameraOff, BarChart3,
  Plus, RefreshCw, AlertCircle, CheckCircle, XCircle,
  User, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TelehealthCenter = () => {
  const [sessions, setSessions] = useState([]);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [showNewSession, setShowNewSession] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  
  const [newSessionForm, setNewSessionForm] = useState({
    patient_id: '',
    provider_id: '',
    session_type: 'video_visit',
    scheduled_time: '',
    duration_minutes: 30,
    notes: '',
    waiting_room_enabled: true
  });

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSessions(),
        loadHistory(),
        loadStats()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const loadSessions = async () => {
    try {
      // Use demo_provider as default user_id for upcoming sessions
      const userId = 'demo_provider';
      const response = await fetch(`${API_URL}/api/phase27/telehealth/upcoming/${userId}?role=provider`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadHistory = async () => {
    try {
      // Use demo_provider as default user_id for session history
      const userId = 'demo_provider';
      const response = await fetch(`${API_URL}/api/phase27/telehealth/history/${userId}?role=provider`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.sessions || []);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase27/telehealth/stats`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const createSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase27/telehealth/sessions?created_by=demo_user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(newSessionForm)
      });

      if (response.ok) {
        toast.success('Telehealth session scheduled successfully');
        setShowNewSession(false);
        loadSessions();
        setNewSessionForm({
          patient_id: '',
          provider_id: '',
          session_type: 'video_visit',
          scheduled_time: '',
          duration_minutes: 30,
          notes: '',
          waiting_room_enabled: true
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create session');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const sessionAction = async (sessionId, action, extra = {}) => {
    try {
      const response = await fetch(`${API_URL}/api/phase27/telehealth/sessions/${sessionId}/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ action, ...extra })
      });

      if (response.ok) {
        toast.success(`Session ${action}ed successfully`);
        loadSessions();
        loadHistory();
        if (action === 'end' || action === 'cancel') {
          setActiveSession(null);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to ${action} session`);
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const joinSession = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase27/telehealth/sessions/${sessionId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          user_name: 'Current User',
          user_type: 'provider'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setActiveSession({ id: sessionId, ...data });
        loadChatMessages(sessionId);
        toast.success('Joined session successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to join session');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const loadChatMessages = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase27/telehealth/sessions/${sessionId}/chat`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setChatMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error loading chat:', error);
    }
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim() || !activeSession) return;

    try {
      const response = await fetch(`${API_URL}/api/phase27/telehealth/sessions/${activeSession.id}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ message: newMessage })
      });

      if (response.ok) {
        setNewMessage('');
        loadChatMessages(activeSession.id);
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return (
      <Badge className={styles[status] || 'bg-gray-100'}>
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="telehealth-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // Active session view
  if (activeSession) {
    return (
      <div className="space-y-4" data-testid="telehealth-active-session">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Video className="h-6 w-6 text-teal-600" />
            Active Telehealth Session
          </h2>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setActiveSession(null)}>
              Minimize
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => sessionAction(activeSession.id, 'end')}
            >
              <PhoneOff className="h-4 w-4 mr-2" />
              End Call
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Video Area */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-0">
                <div className="bg-gray-900 h-96 rounded-t-lg flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <Video className="h-16 w-16 mx-auto mb-4" />
                    <p>Video Conference Area</p>
                    <p className="text-sm">Room Code: {activeSession.room_code}</p>
                  </div>
                </div>
                <div className="p-4 bg-gray-800 rounded-b-lg flex justify-center gap-4">
                  <Button variant="outline" size="icon" className="rounded-full">
                    <Mic className="h-5 w-5" />
                  </Button>
                  <Button variant="outline" size="icon" className="rounded-full">
                    <Camera className="h-5 w-5" />
                  </Button>
                  <Button variant="outline" size="icon" className="rounded-full">
                    <Monitor className="h-5 w-5" />
                  </Button>
                  <Button variant="destructive" size="icon" className="rounded-full" onClick={() => sessionAction(activeSession.id, 'end')}>
                    <PhoneOff className="h-5 w-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chat Area */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Session Chat
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 overflow-y-auto border rounded-lg p-3 mb-3 space-y-2">
                {chatMessages.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center">No messages yet</p>
                ) : (
                  chatMessages.map((msg, idx) => (
                    <div key={idx} className="text-sm">
                      <span className="font-medium">{msg.user_name}: </span>
                      <span>{msg.message}</span>
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                />
                <Button onClick={sendChatMessage}>Send</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="telehealth-center">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Video className="h-7 w-7 text-teal-600" />
            Telehealth Center
          </h1>
          <p className="text-gray-500">Manage virtual visits and video consultations</p>
        </div>
        <Dialog open={showNewSession} onOpenChange={setShowNewSession}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700" data-testid="new-session-btn">
              <Plus className="h-4 w-4 mr-2" />
              Schedule Session
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Schedule Telehealth Session</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Patient ID</Label>
                <Input
                  value={newSessionForm.patient_id}
                  onChange={(e) => setNewSessionForm({...newSessionForm, patient_id: e.target.value})}
                  placeholder="Enter patient ID"
                />
              </div>
              <div>
                <Label>Provider ID</Label>
                <Input
                  value={newSessionForm.provider_id}
                  onChange={(e) => setNewSessionForm({...newSessionForm, provider_id: e.target.value})}
                  placeholder="Enter provider ID"
                />
              </div>
              <div>
                <Label>Session Type</Label>
                <Select
                  value={newSessionForm.session_type}
                  onValueChange={(v) => setNewSessionForm({...newSessionForm, session_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video_visit">Video Visit</SelectItem>
                    <SelectItem value="phone_call">Phone Call</SelectItem>
                    <SelectItem value="family_conference">Family Conference</SelectItem>
                    <SelectItem value="team_meeting">Team Meeting</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Scheduled Time</Label>
                <Input
                  type="datetime-local"
                  value={newSessionForm.scheduled_time}
                  onChange={(e) => setNewSessionForm({...newSessionForm, scheduled_time: e.target.value})}
                />
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Select
                  value={String(newSessionForm.duration_minutes)}
                  onValueChange={(v) => setNewSessionForm({...newSessionForm, duration_minutes: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">60 minutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={newSessionForm.notes}
                  onChange={(e) => setNewSessionForm({...newSessionForm, notes: e.target.value})}
                  placeholder="Session notes..."
                />
              </div>
              <Button className="w-full" onClick={createSession}>
                Schedule Session
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Sessions</p>
                  <p className="text-2xl font-bold">{stats.total_sessions}</p>
                </div>
                <Video className="h-8 w-8 text-teal-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Avg Duration</p>
                  <p className="text-2xl font-bold">{stats.avg_duration_minutes} min</p>
                </div>
                <Clock className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Hours</p>
                  <p className="text-2xl font-bold">{stats.total_duration_hours}h</p>
                </div>
                <BarChart3 className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Upcoming</p>
                  <p className="text-2xl font-bold">{sessions.length}</p>
                </div>
                <Calendar className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sessions Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="upcoming">Upcoming Sessions</TabsTrigger>
          <TabsTrigger value="history">Session History</TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="mt-4">
          {sessions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No upcoming sessions</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setShowNewSession(true)}
                >
                  Schedule a Session
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => (
                <Card key={session.id} data-testid={`session-${session.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-teal-100 rounded-full">
                          <Video className="h-6 w-6 text-teal-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{session.patient_name}</h3>
                            {getStatusBadge(session.status)}
                          </div>
                          <p className="text-sm text-gray-500">
                            Provider: {session.provider_name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatDateTime(session.scheduled_time)} • {session.duration_minutes} min
                          </p>
                          <p className="text-xs text-gray-400">
                            Room: {session.room_code}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {session.status === 'scheduled' && (
                          <>
                            <Button 
                              size="sm"
                              onClick={() => sessionAction(session.id, 'start')}
                            >
                              <Play className="h-4 w-4 mr-1" />
                              Start
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => joinSession(session.id)}
                            >
                              Join
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => sessionAction(session.id, 'cancel', { reason: 'Cancelled by user' })}
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                        {session.status === 'in_progress' && (
                          <>
                            <Button 
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => joinSession(session.id)}
                            >
                              <Video className="h-4 w-4 mr-1" />
                              Join Now
                            </Button>
                            <Button 
                              size="sm"
                              variant="destructive"
                              onClick={() => sessionAction(session.id, 'end')}
                            >
                              End
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {history.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No session history</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {history.map((session) => (
                <Card key={session.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-gray-100 rounded-full">
                          <Video className="h-6 w-6 text-gray-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{session.patient_name}</h3>
                            {getStatusBadge(session.status)}
                          </div>
                          <p className="text-sm text-gray-500">
                            Provider: {session.provider_name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatDateTime(session.ended_at)} • Duration: {session.actual_duration} min
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TelehealthCenter;
